import { Controller } from "@hotwired/stimulus"

// Rich hover card for the "Update" table column. The trigger element wraps
// a <template> with the card's HTML; on hover/focus we clone it into a
// body-level fixed card so the table's overflow container can't clip it.
// Opens to the LEFT of the icon (the column sits at the table's far right),
// flips right when there's no room, and clamps vertically to the viewport.
export default class extends Controller {
  connect() {
    this._show = this.show.bind(this)
    this._hide = this.hide.bind(this)
    this.element.addEventListener("mouseenter", this._show)
    this.element.addEventListener("mouseleave", this._hide)
    this.element.addEventListener("focusin", this._show)
    this.element.addEventListener("focusout", this._hide)
  }

  disconnect() {
    this.hide()
    this.element.removeEventListener("mouseenter", this._show)
    this.element.removeEventListener("mouseleave", this._hide)
    this.element.removeEventListener("focusin", this._show)
    this.element.removeEventListener("focusout", this._hide)
  }

  show() {
    const template = this.element.querySelector("template")
    if (!template) return
    this.hide()

    const card = document.createElement("div")
    // Inline styles on purpose — the card lives outside any Tailwind-scoped
    // wrapper and must never depend on classes that may not be compiled.
    // Width defaults to 400px; a caller can shrink it via data-tip-width.
    const width = this.element.dataset.tipWidth || "400px"
    card.style.cssText =
      // Theme variables, not fixed hexes, so the card is dark in night mode
      // like the rest of the page (Phelipe 2026-10-23); the fallbacks keep
      // light mode as it was.
      "position:fixed;z-index:9999;width:" + width + ";max-width:92vw;background:var(--color-white, #fff);" +
      "color:var(--color-gray-700, #374151);border:1px solid var(--color-gray-200, #e5e7eb);border-radius:12px;box-shadow:0 12px 28px rgba(0,0,0,.14);" +
      "padding:14px 16px;pointer-events:none;opacity:0;transform:translateY(2px);" +
      "transition:opacity .12s ease, transform .12s ease;"
    card.innerHTML = template.innerHTML
    document.body.appendChild(card)

    const rect = this.element.getBoundingClientRect()
    const cardWidth = card.offsetWidth
    const cardHeight = card.offsetHeight
    // Default opens LEFT (built for far-right table columns); pass
    // data-tip-side="right" to open on the right, flipping when cramped.
    let x
    if (this.element.dataset.tipSide === "right") {
      x = rect.right + 10
      if (x + cardWidth > window.innerWidth - 8) x = Math.max(8, rect.left - cardWidth - 10)
    } else {
      x = rect.left - cardWidth - 10
      if (x < 8) x = Math.min(rect.right + 10, window.innerWidth - cardWidth - 8)
    }
    let y = rect.top + rect.height / 2 - cardHeight / 2
    y = Math.max(8, Math.min(y, window.innerHeight - cardHeight - 8))
    card.style.left = `${Math.round(x)}px`
    card.style.top = `${Math.round(y)}px`

    requestAnimationFrame(() => {
      card.style.opacity = "1"
      card.style.transform = "translateY(0)"
    })
    this._card = card
    this._onScroll = this._hide
    window.addEventListener("scroll", this._onScroll, true)
  }

  hide() {
    if (this._card) {
      this._card.remove()
      this._card = null
    }
    if (this._onScroll) {
      window.removeEventListener("scroll", this._onScroll, true)
      this._onScroll = null
    }
  }
}
